import React, { Fragment, useContext, useState } from 'react';
import { UndoIcon } from '@fluentui/react-icons-mdl2';
import { useListPreferenceFlag } from '../../shared/components/table/useListPreferenceFlag';
import SaveSvg from '../../../svg/save.svg';
import MenuSvg from '../../../svg/menu-lines.svg';
import DownArrowSvg from '../../../svg/down-arrow.svg';
import DeleteSvg from '../../../svg/delete.svg';
import { CustomViewContext } from './CustomViewContextProvider';
import { appState, useSetState } from '../../AppState';
import { deleteCustomView, getChildFiltersCountFromStorage, getConfiguredColumnsKeyOrderFromStorage, getSortOrderFromStorage, isChildFilterSelected, isSaveView, resetTicketListViewLayout, selectedColumnsFromConfigurableColumnsKeyOrder, ticketListViewHasPersonalLayout, withPersonalColumnOrder } from './CustomViewPopup/heplers/customPopupHelper';
import { Dialog, Loader, Skeleton, Text, ThemePrepared } from '@fluentui/react-northstar';
import ToolbarCSS from './Toolbar.module.css';
import useSelectedViewName from './CustomViewPopup/useSelectedViewName';
import { ADMINISTRATORS, AGENTS, ANALYSTS, CustomViewType, LICENSE_FEATURE } from '../../shared/utils/constants';
import { getCachedFeature } from '../../shared/cache/FeatureCache';
import { viewTemplateQuestions } from '../List/templateQuestions';
import AppCSS from '../../App.module.css';
import { ThemeColorScheme } from '../../shared/common/TeamsTheme';
import ListFunctions from '../List/ListFunctions';
import { useTranslation } from 'react-i18next';
import { platformService } from '../../shared/services/platform.service';
import SaveCsvIcon from '../../../svg/csv.svg';

interface PropsI {
  defaultFilters: FilterPropsItems[];
  globalTheme: ThemePrepared<any>;
  page: string | undefined;
  isCustomViewEnabled: boolean;
  listFeatureStates: ListFeatureStates;
}

const TicketToolbarSecondary = ({ defaultFilters, globalTheme, page, isCustomViewEnabled, listFeatureStates }: PropsI) => {
  const setAppState = useSetState();
  const currentState = appState()
  const viewName = useSelectedViewName();
  const {t} = useTranslation();

  const { customViewStates, setCustomViewStates } = useContext(CustomViewContext);
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false);
  const [submitClicked, setSubmitClicked] = useState(false);
  const { isDefaultViewId } = ListFunctions();
  const { getFilterQuery } = ListFunctions();
  const [ exporting, setExporting ] = useState<boolean>(false);
  const api = new platformService();

  // Follows the stored settings, so Reset view enables the moment a column is dragged or sorted. Read here, above
  // the early return below, as it is a hook.
  const canResetView = useListPreferenceFlag(ticketListViewHasPersonalLayout);

  // Drawn from the start, the tickets loading under it (Shaun, 2026-10-20). It used to hide while they loaded, so as
  // not to flash "All Active" before a saved view's name (2023); the name now waits for its view instead - a short
  // shimmer while a custom view is still loading, or while a refresh has the view id parked - and so do the links,
  // so Edit View or Delete cannot act on a view not yet known.
  if (page === 'chart') {
    return <></>;
  }
  const nameKnown = !!viewName && viewName !== 'loading...';

  const actionOnCustomView = async (view: ViewType) => {
    if (view === 'edit') {
      setCustomViewStates(
        (prev): CustomViewStatesType => ({
          ...prev,
          showCustomViewModal: true,
          view,
          viewName: prev.selectedCustomView?.Name,
          filters: JSON.parse(
            JSON.stringify(prev.selectedCustomView.FiltersDropdownValues)
          ),
          columns: {
            ...prev.selectedCustomView?.ColumnsSelectedValues,
            sortOrder: prev.selectedCustomView?.OrderBy ? {
              orderBy: prev.selectedCustomView?.OrderBy,
              orderType: prev.selectedCustomView?.OrderType,
            } : null,
          },
        })
      );
    }
    if (view === 'delete') {
      setShowDeleteConfirmationModal(true);
    }
    if (view === 'columns') {
      setCustomViewStates((prev): CustomViewStatesType => ({
        ...prev,
        showCustomViewModal: true,
        view,
        viewName: '',
        filters: JSON.parse(JSON.stringify(defaultFilters)),
        columns: {
          ...prev.columns,
          columnsKeyOrder: withPersonalColumnOrder(getConfiguredColumnsKeyOrderFromStorage()),
          selectedColumns: selectedColumnsFromConfigurableColumnsKeyOrder(currentState, withPersonalColumnOrder(getConfiguredColumnsKeyOrderFromStorage()), listFeatureStates),
          sortOrder: getSortOrderFromStorage()
        }
      }));
    }
    if (view === 'save') {
      const count = await getChildFiltersCountFromStorage();
      setCustomViewStates((prev): CustomViewStatesType => ({
        ...prev,
        showCustomViewModal: true,
        view,
        ...(isChildFilterSelected(count) && {
          filters: JSON.parse(
            JSON.stringify(currentState.filterDropdown.selectedValues)
          ),
        }),
        columns: {
          ...prev.columns,
          sortOrder: prev.configureColumnsApplied.sortOrder
        }
      }));
    }
  };

  const isAdminUser = currentState?.userRoles?.roles?.includes(ADMINISTRATORS) ?? false;
  const isAnalystUser = currentState?.userRoles?.roles?.includes(ANALYSTS) ?? false;
  const isAgent = currentState?.userRoles?.roles?.includes(AGENTS) ?? false;
  const isEditEnabled = (customViewStates.view === 'edit' || customViewStates.selectedCustomView);
  const { customViews, selectedCustomView } = customViewStates;
  const showEditView = isAgent && selectedCustomView && customViews.some(({ Id, CreatedById }) => Id === selectedCustomView.Id && CreatedById === currentState.currentUserId);


  const onResetView = () => {
    if (!canResetView) return;
    const appliedSortOrder = getSortOrderFromStorage() || customViewStates.configureColumnsApplied.sortOrder;
    resetTicketListViewLayout();
    const viewSortOrder = selectedCustomView?.OrderBy
      ? { orderBy: selectedCustomView.OrderBy, orderType: selectedCustomView.OrderType }
      : null;
    // A change of sort here is what refetches the list (CustomViewPopup), so it reloads only if the sort moved -
    // and then with the rows left on screen while it loads, as My Work's Reset View does.
    setCustomViewStates((prev): CustomViewStatesType => ({
      ...prev,
      configureColumnsApplied: { prevSortOrder: appliedSortOrder, sortOrder: viewSortOrder, keepRows: true },
    }));
  };

  const onCancel = () => {
    setShowDeleteConfirmationModal(false);
  };

  const onSubmit = async () => {
    if (isCustomViewEnabled) {
      setSubmitClicked(true);
      await deleteCustomView({defaultFilters, customViewStates, setCustomViewStates, setAppState });
      setShowDeleteConfirmationModal(false);
      setSubmitClicked(false);
    }
  };

  const onExportClick = async () => {
    setExporting(true);
    let filter = await getFilterQuery(customViewStates, undefined, undefined, undefined, true);
    // Every question of the view's template, written after the custom properties - all of them, as every custom property is.
    const formQuestions = await getCachedFeature(LICENSE_FEATURE.TicketTemplates) ? await viewTemplateQuestions(customViewStates?.selectedCustomView) : [];
    if (formQuestions.length > 0)
      filter = [filter, `formcolumns=${encodeURIComponent(formQuestions.map(question => question.formQuestion).join(','))}`].filter(Boolean).join('&');
    await api.exportTicketsToCsv(`${filter}`);
    setExporting(false);   
  }

  const colorScheme = globalTheme.siteVariables.colorScheme;

  return (
    <Fragment>     
        <Dialog
          content={ <Loader label={t("ticket.toolbar.export-tickets")} size="large" labelPosition="below" /> }
          trapFocus={true}
          open={exporting}
          style={{maxWidth: "400px"}}
        />              
      <div
        className={`flex justify-between -mt-5 pl-4 pr-4 pt-2 pb-2 ${ToolbarCSS['toolbar-custom-views']}`}
        style={{ backgroundColor: colorScheme.default.background, ...ThemeColorScheme(globalTheme.siteVariables) }}
      >
        <div className='flex space-x-4'>
          {nameKnown ? (
            <Text content={viewName} weight='semibold' />
          ) : (
            <Skeleton animation='wave' aria-hidden>
              <Skeleton.Line width='120px' height='16px' style={{ marginTop: 3 }} />
            </Skeleton>
          )}
          {nameKnown && isAdminUser && isCustomViewEnabled &&
            (isSaveView(
                isDefaultViewId,
                customViewStates.configurableColumnsKeyOrder,
                currentState.filterDropdownCount
              )) && (
            <div
              className={`hidden md:flex font-semibold space-x-1 items-center cursor-pointer`}
              style={{ color: colorScheme.brand.foreground }}
              onClick={() => actionOnCustomView('save')}
            >
              <SaveSvg className={ToolbarCSS['brand-svg-icon']} />
              <span>{t('ticket.ticket-list.configure-column.save-view')}</span>
            </div>
          )}
          {nameKnown && (isAdminUser || isAnalystUser || showEditView) && isEditEnabled && 
            <div
              className={`hidden md:flex font-semibold space-x-1 items-center cursor-pointer`}
              style={{ color: colorScheme.brand.foreground }}
              onClick={() => actionOnCustomView('edit')}
            >
              <SaveSvg className={ToolbarCSS['brand-svg-icon']} />
              <span>{t('ticket.ticket-list.configure-column.edit-view')}</span>
            </div>
          }

          {nameKnown && page != 'board' &&
            <button
              type='button'
              className={`hidden md:flex font-semibold space-x-1 items-center ${canResetView ? 'cursor-pointer' : 'cursor-default'}`}
              style={{ color: canResetView ? colorScheme.brand.foreground : colorScheme.default.foregroundDisabled }}
              onClick={onResetView}
              disabled={!canResetView}
            >
              <UndoIcon />
              <span>{t('ticket.ticket-list.configure-column.reset-view')}</span>
            </button>
          }

          {nameKnown && (customViewStates.selectedCustomView?.Type!=CustomViewType.DefaultView && (isAdminUser || isAnalystUser || showEditView)) && 
            <div
              className={`hidden md:flex font-semibold space-x-1 items-center cursor-pointer`}
              style={{ color: colorScheme.brand.foreground }}
              onClick={() => actionOnCustomView('delete')}
            >
              <DeleteSvg className={ToolbarCSS['brand-svg-icon']} />
              <span>{t('ticket.ticket-board.delete')}</span>
            </div>
          }
        </div>
        {nameKnown && (isAdminUser || isAnalystUser) && isCustomViewEnabled && page != 'board' && !customViewStates.selectedCustomView && (
          <>
          <div
            className={`hidden md:flex font-semibold space-x-1 items-center cursor-pointer`}
            style={{ color: colorScheme.brand.foreground, marginLeft: "auto", marginRight: "10px" }}
            onClick={() => actionOnCustomView('columns')}
          >
            <div className='flex' style={{marginBottom: "-2px"}}>
              <MenuSvg className={ToolbarCSS['brand-svg-icon']} />
              <DownArrowSvg className={ToolbarCSS['brand-svg-icon']} />
            </div>
            <span>{t('ticket.ticket-list.configure-column.configure-columns')}</span>
          </div>
          </>
        )}
         {nameKnown && (isAdminUser || isAnalystUser) && (
          <>
          <div
            className={`hidden md:flex font-semibold space-x-1 items-center cursor-pointer`}
            style={{ color: colorScheme.brand.foreground }}
            onClick={() => onExportClick()}
          >
            <div className='flex' style={{marginBottom: "-3px"}}>
             <SaveCsvIcon className={ToolbarCSS['brand-svg-icon']} />
            </div>
            <span>{t('ticket.toolbar.export-to-csv')}</span>
          </div>
          </>
         )}
      </div>
      <Dialog
        className={`${ToolbarCSS['deleteCustomViewDialog']} ${AppCSS['confirmDialog']}`}
        closeOnOutsideClick={false}
        cancelButton={{ content: t('common.buttons.cancel'), disabled: submitClicked }}
        onCancel={onCancel}
        confirmButton={{
          content: t('common.buttons.delete'),
          primary: true,
          disabled: submitClicked,
        }}
        onConfirm={onSubmit}
        open={showDeleteConfirmationModal}
        header={{
          content: (
            <div>
              <h3>{t('ticket.custom-view.delete-prompt-header', { viewName: customViewStates.selectedCustomView?.Name })}</h3>
              <div className='text-sm font-medium mt-2' style={{ color: colorScheme.default.foreground2 }}>
                {t('ticket.custom-view.delete-prompt')}
              </div>
            </div>
          ),
          style: { width: '100%' },
        }}
      ></Dialog>
    </Fragment>
  );
};

export default TicketToolbarSecondary;
